import { storeToRefs } from 'pinia'
import { watch } from 'vue'

import { accountApi } from '@/api'
import { useAuth } from '@/composables/auth/useAuth'
import { useMainStore } from '@/stores/main'
import { useSiteConfigStore } from '@/stores/siteConfig'

const TG_WEBAPP_READY_TIMEOUT_MS = 2000
const TG_WEBAPP_POLL_INTERVAL_MS = 50
// Telegram Login Widget callback 在 popup 被 block / 注入失敗時可能永不觸發
// （官方未保證 callback 一定會呼叫），避免 re-entry lock 永久卡死
const TG_WIDGET_CALLBACK_TIMEOUT_MS = 120000
const TG_SCRIPT_LOAD_TIMEOUT_MS = 30000

const TG_WEBAPP_SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js'
const TG_LOGIN_SCRIPT_SRC = 'https://telegram.org/js/telegram-login.js'

// WebApp initData (signed): 非空 URL-encoded string
// WebApp initDataUnsafe: 只要是有 key 的物件即視為可用，後端會再驗簽
// 嚴格判斷 user.id / auth_date 會誤殺 Android 9.6 encode bug 後 recover 出的合法 payload
const isValidTelegramAuthData = (data) => {
  if (data === null || data === undefined) return false
  if (typeof data === 'boolean' || typeof data === 'number') return false
  if (typeof data === 'string') return data.length > 0
  if (typeof data === 'object') return Object.keys(data).length > 0
  return false
}

const isValidWebAppInitData = (data) => {
  return data !== null && typeof data === 'object' && Object.keys(data).length > 0
}

// OIDC Widget callback payload: { id_token: "<JWT>" }
const isValidTelegramIdTokenPayload = (data) => {
  return data && typeof data === 'object' && typeof data.id_token === 'string' && data.id_token.length > 0
}

// 已知兩種 Telegram client 提供 initData 的破損形式，皆會讓 SDK 解析失敗：
//
// (1) Android 9.6 + URL 結尾 "/" mini app：整段 hash 被多 encode 一次（%7B → %257B），
//     initDataUnsafe 只剩 query_id。
//
// (2) macOS 9.6 client：hash 中 user / tgWebAppThemeParams 的 JSON 值未做 URL encode（raw 形式），
//     {} , 等字元混入 URL 結構，URLSearchParams 解析失敗，initDataUnsafe 只剩錯亂的 user 字串。
//     hash 形式範例：
//       #tgWebAppData=user={"id":...}&chat_instance=...&auth_date=...&signature=...&hash=...&tgWebAppVersion=...&tgWebAppPlatform=macos&tgWebAppThemeParams={...}
//
// 修復策略：先處理 case 1 的雙層 encode，再用「大括號配對」抽出 raw JSON 欄位，
// 剩餘 query 用 URLSearchParams 解析，最後重組成 SDK 應有的 initData / initDataUnsafe。
function recoverInitDataFromHash() {
  if (typeof window === 'undefined') return null
  const rawHash = window.location.hash
  if (!rawHash || !rawHash.includes('tgWebAppData')) return null

  let body = rawHash.replace(/^#/, '')

  // case 1: 整段被 encode（連結構符 = & 都變 %3D %26）
  if (body.startsWith('tgWebAppData%3D') || body.includes('%26tgWebAppPlatform')) {
    try {
      body = decodeURIComponent(body)
    } catch (e) {
      return null
    }
  }

  // body 形式應為 tgWebAppData=...&tgWebAppVersion=...&tgWebAppPlatform=...
  // 但 tgWebAppData value 本身可能含 raw JSON（macOS 9.6），不能直接用 split('&')
  // 策略：tgWebAppData 一定是 body 第一段（以 tgWebAppData= 開頭），它的結束以後續的
  // &tgWebAppVersion= / &tgWebAppPlatform= 為界（這兩個 key 不會出現在 initData 內）
  if (!body.startsWith('tgWebAppData=')) return null
  let initDataRaw = body.slice('tgWebAppData='.length)

  const versionIdx = initDataRaw.indexOf('&tgWebAppVersion=')
  const platformIdx = initDataRaw.indexOf('&tgWebAppPlatform=')
  let cutIdx = -1
  if (versionIdx >= 0 && platformIdx >= 0) cutIdx = Math.min(versionIdx, platformIdx)
  else if (versionIdx >= 0) cutIdx = versionIdx
  else if (platformIdx >= 0) cutIdx = platformIdx
  if (cutIdx >= 0) initDataRaw = initDataRaw.slice(0, cutIdx)

  // case 2 偵測：raw JSON 形式（user={"id":...）
  // 正常 encoded 形式會是 user=%7B%22id%22... 或 user=%7B...，不會有未 encode 的 {
  const hasRawJson = /=\{/.test(initDataRaw)

  // case 1 殘留偵測：value 中含 %25 開頭（雙重 encode）
  if (/%25[0-9A-Fa-f]{2}/.test(initDataRaw) && !hasRawJson) {
    try {
      initDataRaw = decodeURIComponent(initDataRaw)
    } catch (e) {
      return null
    }
  }

  const initDataUnsafe = {}
  // pairs 保留原始 JSON 字串（pre-parse）供後端 Ed25519 驗章使用
  // 不能 JSON.parse → JSON.stringify 重組，會丟失 Telegram 的 \/ escape，導致簽章不一致
  const pairs = []
  let remaining = initDataRaw

  // 兼容兩種破損形式的解析迴圈：
  //   - macOS 9.6：value 開頭是 raw `{`，用大括號配對抓 JSON
  //   - Android 9.6 (case 1 decode 後)：value 開頭是 `%7B`，先一般切分再 parse JSON
  //   - 一般 scalar 欄位（auth_date / chat_instance / hash / signature）：用 & 切分
  while (remaining.length > 0) {
    const eqIdx = remaining.indexOf('=')
    if (eqIdx < 0) break
    const key = remaining.slice(0, eqIdx)
    const after = remaining.slice(eqIdx + 1)

    let rawValue
    let nextStart

    if (after.startsWith('{')) {
      // macOS 9.6 形式：raw JSON 直接塞進 URL，須做大括號配對找結束位置
      // user JSON 內不會出現未 escape 的 {}，因為 string value 內也會是 %7B/%7D 或 \u 形式
      let depth = 0
      let endIdx = -1
      for (let i = 0; i < after.length; i++) {
        const ch = after[i]
        if (ch === '{') depth++
        else if (ch === '}') {
          depth--
          if (depth === 0) {
            endIdx = i
            break
          }
        }
      }
      if (endIdx < 0) return null
      rawValue = after.slice(0, endIdx + 1)
      const rest = after.slice(endIdx + 1)
      nextStart = rest.startsWith('&') ? rest.slice(1) : rest
    } else {
      // 正常 URL 形式：value 結束於下一個 & 或字串結尾
      // 涵蓋 Android 9.6 case 1 decode 後的 %7B...%7D 形式，以及一般 scalar
      const ampIdx = after.indexOf('&')
      rawValue = ampIdx < 0 ? after : after.slice(0, ampIdx)
      nextStart = ampIdx < 0 ? '' : after.slice(ampIdx + 1)
    }

    // 先 decode 得到 Telegram 原始字串內容（含 \/ escape）
    let decoded
    try {
      decoded = decodeURIComponent(rawValue)
    } catch (e) {
      decoded = rawValue
    }

    // initDataUnsafe：前端使用方便起見，JSON 欄位 parse 成物件
    // pairs：保留 Telegram 原始 JSON 字串（含 \/ 等 escape），供 Ed25519 驗簽
    if (decoded.startsWith('{') && decoded.endsWith('}')) {
      try {
        initDataUnsafe[key] = JSON.parse(decoded)
      } catch (e) {
        initDataUnsafe[key] = decoded
      }
    } else {
      initDataUnsafe[key] = decoded
    }
    pairs.push([key, decoded])

    remaining = nextStart
  }

  // 重組後端 Ed25519 驗簽要的 raw initData 字串
  // 關鍵：value 必須是 Telegram 原始字串重新 encode，不能經過 JSON.parse / stringify round-trip
  // 否則 user JSON 內的 \/ 會被還原成 /，導致 dataCheckString 與 Telegram 簽章原文不一致
  const reEncoded = pairs.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')

  return { initData: reEncoded, initDataUnsafe }
}

let webAppPromise = null
let loginPromise = null

function injectScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true'
        resolve()
      },
      { once: true }
    )
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true })
    document.head.appendChild(script)
  })
}

function withTimeout(promise, label) {
  const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} load timeout`)), TG_SCRIPT_LOAD_TIMEOUT_MS))
  return Promise.race([promise, timeoutPromise])
}

// telegram-web-app.js：Mini App SDK，client 端可無條件預載入（無 client_id 依賴）
function loadWebAppScript() {
  if (webAppPromise) return webAppPromise
  webAppPromise = withTimeout(injectScript(TG_WEBAPP_SCRIPT_SRC), 'telegram-web-app.js').catch((err) => {
    webAppPromise = null
    throw err
  })
  return webAppPromise
}

// telegram-login.js：OIDC Web Widget，需取得 clientID 後才有意義
function loadLoginScript() {
  if (loginPromise) return loginPromise
  loginPromise = withTimeout(injectScript(TG_LOGIN_SCRIPT_SRC), 'telegram-login.js').catch((err) => {
    loginPromise = null
    throw err
  })
  return loginPromise
}

export default defineNuxtPlugin(() => {
  const { $request } = useNuxtApp()
  const config = useRuntimeConfig()
  const store = useMainStore()
  const siteConfigStore = useSiteConfigStore()
  const { telegram } = storeToRefs(siteConfigStore)
  const auth = useAuth()

  let isInFlight = false

  const getClientId = () => {
    const fromSiteConfig = telegram.value?.clientID
    const fromEnv = config.public.telegramClientId
    return fromSiteConfig || fromEnv || ''
  }

  const beginFlight = () => {
    if (isInFlight) return false
    isInFlight = true
    store.setLoading({ thirdPartyLogin: true })
    return true
  }

  const endFlight = () => {
    isInFlight = false
    store.setLoading({ thirdPartyLogin: false })
  }

  function toggleButton(buttonType, action) {
    const { Telegram } = window || {}
    const tgWebApp = Telegram?.WebApp || {}
    const tgButton = tgWebApp[buttonType] || {}
    if (action === 'show') {
      tgButton.show()
    } else if (action === 'hide') {
      tgButton.hide()
    }
  }

  function setButtonClickEvt(buttonType, event) {
    const { Telegram } = window || {}
    const tgWebApp = Telegram?.WebApp || {}
    const tgButton = tgWebApp[buttonType] || {}
    tgButton.onClick(event)
  }

  function requestFullScree() {
    const { Telegram } = window || {}
    const tgWebApp = Telegram?.WebApp || {}
    tgWebApp.requestFullscreen()
  }

  async function waitForTelegramWebApp() {
    const start = Date.now()
    while (Date.now() - start < TG_WEBAPP_READY_TIMEOUT_MS) {
      const tgWebApp = window?.Telegram?.WebApp
      if (tgWebApp && (isValidWebAppInitData(tgWebApp.initDataUnsafe) || tgWebApp.initData)) {
        return tgWebApp
      }
      await new Promise((resolve) => setTimeout(resolve, TG_WEBAPP_POLL_INTERVAL_MS))
    }
    return null
  }

  async function bindingTelegram({ loginType, authData, isSignature, idToken }) {
    if (loginType === 'Widget' && !idToken) {
      console.error('[Telegram] bindingTelegram aborted: Widget missing idToken')
      return
    }

    if (loginType === 'WebApp' && !isValidTelegramAuthData(authData)) {
      alert('[Telegram] bindingTelegram aborted: invalid authData')
      return
    }

    try {
      await $request.send(
        accountApi.applyBinding({
          provider: 'telegram',
          loginType,
          additionalUserInfo: authData,
          isSignature,
          idToken,
        })
      )

      window.dispatchEvent(new CustomEvent('third-party-bound'))
    } catch (error) {
      console.error('Telegram Bind Fail', error)
    }
  }

  async function callTelegramLogin({ loginType, authData, isSignature, idToken }) {
    if (loginType === 'Widget' && !idToken) {
      console.error('[Telegram] callTelegramLogin aborted: Widget missing idToken')
      return
    }

    if (loginType === 'WebApp' && !isValidTelegramAuthData(authData)) {
      alert(`[Telegram] callTelegramLogin aborted: invalid authData\n${JSON.stringify(authData)}`)
      return
    }

    try {
      await auth.loginWithTelegram({ loginType, additionalUserInfo: authData, isSignature, idToken })
    } catch (error) {
      console.error('Telegram Login Fail !', error)
    }
  }

  async function handleWebAppFlow(isBinding) {
    const tgWebApp = await waitForTelegramWebApp()
    if (!tgWebApp) {
      alert('[Telegram] WebApp init data unavailable after timeout')
      return
    }

    let { initDataUnsafe, initData } = tgWebApp

    // SDK 解析結果不完整（Android 9.6 雙重 encode bug），改從 raw hash 自行解析
    const sdkBroken = !initDataUnsafe || !initDataUnsafe.user || !initDataUnsafe.auth_date
    if (sdkBroken) {
      const recovered = recoverInitDataFromHash()
      if (recovered) {
        initData = recovered.initData
        initDataUnsafe = recovered.initDataUnsafe
      }
    }

    const rawHash = typeof window !== 'undefined' ? window.location.hash : ''
    // alert(`[TG Debug A] rawHash:\n${rawHash}`)
    // alert(
    //   `[TG Debug B]\n` +
    //     `UA: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a'}\n` +
    //     `TG platform: ${tgWebApp.platform}\n` +
    //     `TG version: ${tgWebApp.version}\n` +
    //     `sdkBroken: ${sdkBroken}\n` +
    //     `hash len: ${rawHash.length}\n` +
    //     `hash has %257B: ${rawHash.includes('%257B')}\n` +
    //     `initData len: ${initData ? initData.length : 0}\n` +
    //     `initData(0..200): ${initData ? initData.slice(0, 200) : 'EMPTY'}\n` +
    //     `initDataUnsafe keys: ${initDataUnsafe ? Object.keys(initDataUnsafe).join(',') : 'NULL'}\n` +
    //     `has user: ${!!initDataUnsafe?.user}\n` +
    //     `user.id: ${initDataUnsafe?.user?.id ?? 'n/a'}\n` +
    //     `auth_date: ${initDataUnsafe?.auth_date ?? 'n/a'}\n` +
    //     `signature: ${initDataUnsafe?.signature ? 'present' : 'absent'}`
    // )

    const useSignedData = Boolean(initDataUnsafe?.signature)
    const authData = useSignedData ? initData : initDataUnsafe

    if (isBinding) {
      await bindingTelegram({ loginType: 'WebApp', authData, isSignature: useSignedData })
    } else {
      await callTelegramLogin({ loginType: 'WebApp', authData, isSignature: useSignedData })
    }
  }

  function handleWidgetFlow(isBinding) {
    const clientIdRaw = getClientId()
    if (!clientIdRaw) {
      console.warn('[Telegram] Client ID missing, cannot init Login (OIDC) widget')
      endFlight()
      return
    }

    const client_id = Number(clientIdRaw)
    if (!Number.isFinite(client_id) || client_id <= 0) {
      console.error('[Telegram] Invalid Telegram clientID, expected numeric:', clientIdRaw)
      endFlight()
      return
    }

    const tgLoginApi = window?.Telegram?.Login
    if (!tgLoginApi?.auth) {
      console.error('[Telegram] telegram-login.js not loaded')
      endFlight()
      return
    }

    let settled = false
    const safetyTimer = setTimeout(() => {
      if (settled) return
      settled = true
      console.warn('[Telegram] Login callback did not fire within timeout, releasing lock')
      endFlight()
    }, TG_WIDGET_CALLBACK_TIMEOUT_MS)

    // 必須在同一個 user-gesture tick 內同步呼叫，避免 popup 被瀏覽器擋
    // (見 docs/telegram-login-migration.md 常見錯誤 #6)
    tgLoginApi.auth(
      {
        client_id,
        request_access: ['write'],
      },
      async (data) => {
        if (settled) return
        settled = true
        clearTimeout(safetyTimer)

        try {
          if (data?.error) {
            console.warn('[Telegram] Login error from popup:', data.error)
            return
          }
          if (!isValidTelegramIdTokenPayload(data)) {
            // 使用者取消 / popup 被擋 / Telegram 回傳異常 shape
            console.warn('[Telegram] Invalid OIDC callback payload', data)
            return
          }

          if (isBinding) {
            await bindingTelegram({ loginType: 'Widget', isSignature: false, idToken: data.id_token })
          } else {
            await callTelegramLogin({ loginType: 'Widget', isSignature: false, idToken: data.id_token })
          }
        } finally {
          endFlight()
        }
      }
    )
  }

  async function tgLogin(loginType, isBinding = false) {
    if (!beginFlight()) {
      alert('[Telegram] tgLogin ignored: another login is in flight')
      return
    }

    try {
      if (loginType === 'WebApp') {
        try {
          await handleWebAppFlow(isBinding)
        } finally {
          endFlight()
        }
        return
      }

      if (loginType === 'Widget') {
        // Widget 走非同步 callback，endFlight 在 callback 內觸發
        handleWidgetFlow(isBinding)
        return
      }

      alert('[Telegram] Unknown loginType:', loginType)
      endFlight()
    } catch (error) {
      alert('[Telegram] tgLogin unexpected error', error)
      endFlight()
    }
  }

  // Mini App SDK 一開站就預載（與舊版靜態 <script> 行為對齊，確保 Mini App 進來能立即讀 window.Telegram.WebApp）
  loadWebAppScript().catch((err) => {
    alert('[Telegram] Failed to preload telegram-web-app.js:', err)
  })

  // OIDC Login script 等取得 clientID 後預載入，避免使用者點擊時還在等 script load 導致 popup 被擋
  watch(
    () => getClientId(),
    (clientId) => {
      if (!clientId) return
      loadLoginScript().catch((err) => {
        console.error('[Telegram] Failed to preload telegram-login.js:', err)
      })
    },
    { immediate: true }
  )

  return {
    provide: {
      tgWebAppShowBtn: (buttonType) => toggleButton(buttonType, 'show'),
      tgWebAppHideBtn: (buttonType) => toggleButton(buttonType, 'hide'),
      tgWebAppSetBtnEvt: (buttonType, event) => setButtonClickEvt(buttonType, event),
      tgWebAppFullScreen: () => requestFullScree(),
      tgWebAppReady: () => loadWebAppScript(),
      tgLogin,
    },
  }
})
